import {
  AutoAwesome,
  Close,
  ContentCopy,
  ContentCut,
  Edit,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { TruncatedText } from '../CollectionCard/TruncatedText';
import { formatTime } from '../../util/time';
import { ClipTransportBar } from './ClipTransportBar';
import type { ClipTransportBarProps } from './ClipTransportBar';
import * as styles from './TrackClipsModal.styles';
import type { CustomClipSummary, TrackWithClips } from './types';

export interface TrackClipsModalProps {
  /** The track whose clips are shown. Null closes the modal. */
  track: TrackWithClips | null;
  onClose: () => void;
  onCreateClip: () => void;
  onEditClip: (clip: CustomClipSummary) => void;
  onPlayPrimary?: () => void;
  onPlayCustom?: (clipId: number) => void;
  isClipPlaying?: (clipId: number) => boolean;
  onFindSimilar?: () => void;
  /**
   * Playback transport wired to the shared global player state. When provided,
   * a synced play/seek/volume bar renders above the footer.
   */
  transport?: ClipTransportBarProps;
}

export const TrackClipsModal: React.FC<TrackClipsModalProps> = ({
  track,
  onClose,
  onCreateClip,
  onEditClip,
  onPlayPrimary,
  onPlayCustom,
  isClipPlaying,
  onFindSimilar,
  transport,
}) => {
  const { copyToClipboard } = useCopyToClipboard();
  const primaryPlaying = track ? isClipPlaying?.(track.curatedClip.clipId) : false;

  return (
    <Dialog open={track !== null} onClose={onClose} fullWidth maxWidth="sm">
      {track && (
        <>
          <Box sx={styles.header}>
            {track.artworkThumbnailUrl ? (
              <Box
                component="img"
                src={track.artworkThumbnailUrl}
                alt=""
                sx={styles.headerArtwork}
              />
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {track.trackTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {track.artist}
              </Typography>
            </Box>
            <Tooltip title="Copy Track ID" placement="top" arrow>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopy />}
                onClick={() =>
                  copyToClipboard(track.trackId.toString(), 'Track ID')
                }
                aria-label={`Copy Track ID ${track.trackId} to clipboard`}
              >
                Track ID: {track.trackId}
              </Button>
            </Tooltip>
            <IconButton onClick={onClose} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <DialogContent dividers sx={styles.content}>
            <Paper elevation={0} variant="outlined" sx={styles.clipRow}>
              {onPlayPrimary && (
                <IconButton
                  onClick={onPlayPrimary}
                  aria-label={primaryPlaying ? 'Pause primary clip' : 'Play primary clip'}
                >
                  {primaryPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Chip
                  size="small"
                  label={track.hasCuratedClip ? 'Curated clip' : 'Default clip'}
                  color={track.hasCuratedClip ? 'primary' : 'default'}
                  sx={{ mb: 0.5 }}
                />
                <Typography variant="body2" color="text.secondary" sx={styles.timeText}>
                  {formatTime(track.curatedClip.startMs)} →{' '}
                  {formatTime(track.curatedClip.endMs)}
                </Typography>
              </Box>
              {track.hasCuratedClip && (
                <Tooltip title="Copy Clip ID" placement="top" arrow>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={() =>
                      copyToClipboard(track.curatedClip.clipId.toString(), 'Clip ID')
                    }
                    aria-label={`Copy Clip ID ${track.curatedClip.clipId} to clipboard`}
                  >
                    Clip ID: {track.curatedClip.clipId}
                  </Button>
                </Tooltip>
              )}
            </Paper>

            {[...track.customClips]
              .sort((a, b) => b.clipId - a.clipId)
              .map((clip) => {
                const playing = isClipPlaying?.(clip.clipId);
                return (
                  <Paper key={clip.clipId} elevation={0} variant="outlined" sx={styles.clipRow}>
                    {onPlayCustom && (
                      <IconButton
                        onClick={() => onPlayCustom(clip.clipId)}
                        aria-label={playing ? `Pause ${clip.name}` : `Play ${clip.name}`}
                      >
                        {playing ? <Pause /> : <PlayArrow />}
                      </IconButton>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <TruncatedText
                        text={clip.name}
                        component="div"
                        variant="body1"
                        color="text.primary"
                      />
                      <Typography variant="body2" color="text.secondary" sx={styles.timeText}>
                        {formatTime(clip.startMs)} → {formatTime(clip.endMs)}
                      </Typography>
                    </Box>
                    <Tooltip title="Copy Clip ID" placement="top" arrow>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => copyToClipboard(clip.clipId.toString(), 'Clip ID')}
                        aria-label={`Copy Clip ID ${clip.clipId} to clipboard`}
                      >
                        {clip.clipId}
                      </Button>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => onEditClip(clip)}
                      aria-label={`Edit ${clip.name}`}
                    >
                      Edit
                    </Button>
                  </Paper>
                );
              })}
          </DialogContent>

          {transport && <ClipTransportBar {...transport} />}

          <Box sx={styles.footer}>
            {onFindSimilar && (
              <Button
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={onFindSimilar}
              >
                Find similar
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<ContentCut />}
              onClick={onCreateClip}
            >
              Create custom clip
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
};
